import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { ListItemNode, ListNode } from '@lexical/list';
import { type InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HeadingNode } from '@lexical/rich-text';
import clsx from 'clsx';
import type { EditorState, LexicalEditor } from 'lexical';
import { $createParagraphNode, $getRoot } from 'lexical';
import type { CSSProperties, FunctionComponent, ReactNode } from 'react';
import { useCallback, useMemo } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

import { ToolbarPlugin } from './ToolbarPlugin';
import { ValueSyncPlugin } from './ValueSyncPlugin';

export type RichEditorProps = {
    readonly className?: string;
    readonly disabled?: boolean;
    readonly onChange?: (html: string) => void;
    readonly onUserInput?: () => void;
    readonly placeholder?: string;
    readonly staticToolbar?: boolean;
    readonly toolbar?: ReactNode;
    readonly value?: string;
};

export const ToolbarContainer: FunctionComponent<{
    readonly id?: string;
    readonly children: ReactNode;
    readonly className?: string;
    readonly staticMode?: boolean;
    readonly style?: CSSProperties;
}> = ({ id, children, className, staticMode = false, style }) => (
    <div
        className={clsx(
            'bg-gray-50 border border-gray-400 rounded-xl flex gap-xs left-1/2 mx-auto py-xxs px-xxs z-[999]',
            staticMode ? 'sticky transform-none' : 'absolute -translate-x-1/2 -translate-y-[130%]',
            className,
        )}
        id={id}
        style={style}
    >
        {children}
    </div>
);

const lexicalTheme = {
    heading: {
        h1: 'editor-heading-h1',
        h2: 'editor-heading-h2',
        h3: 'editor-heading-h3',
    },
    hr: 'editor-hr',
    list: {
        listitem: 'editor-listitem',
        nested: {
            listitem: 'editor-nested-listitem',
        },
        ol: 'editor-list-ol',
        ul: 'editor-list-ul',
    },
    paragraph: 'editor-paragraph',
    text: {
        bold: 'editor-text-bold',
        underline: 'editor-text-underline',
    },
};

const createEditorConfig = (value: string | undefined): InitialConfigType => ({
    editorState:
        value !== undefined
            ? (editor) => {
                  const parser = new DOMParser();
                  const dom = parser.parseFromString(value, 'text/html');
                  const nodes = $generateNodesFromDOM(editor, dom);
                  editor.update(() => {
                      try {
                          $getRoot()
                              .clear()
                              .append(...nodes);
                          // biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration
                      } catch (error) {
                          // We can only append paragraphs or other root nodes. If we just have
                          //text nodes, we need to create a paragraph node and wrap it.
                          $getRoot().append($createParagraphNode().append(...nodes));
                      }
                  });
              }
            : undefined,
    namespace: 'RichEditor',
    nodes: [HeadingNode, ListNode, ListItemNode, HorizontalRuleNode],
    onError: (error: Error) => {
        throw error;
    },
    theme: lexicalTheme,
});

export const RichEditor: FunctionComponent<RichEditorProps> = ({
    disabled = false,
    onChange,
    onUserInput,
    placeholder = 'Enter text...',
    staticToolbar = false,
    toolbar = (
        <ToolbarContainer id="toolbar" staticMode={staticToolbar}>
            <ToolbarPlugin disabled={disabled} />
        </ToolbarContainer>
    ),
    value,
}) => {
    const handleChange = useCallback(
        (editorState: EditorState, editor: LexicalEditor, tags: Set<string>) => {
            if (tags.has('external-sync')) {
                return;
            }

            if (onChange !== undefined) {
                editorState.read(() => {
                    const html = $generateHtmlFromNodes(editor, null);
                    onChange(html);
                    onUserInput?.();
                });
            }
        },
        [onChange, onUserInput],
    );

    const debouncedChange = useDebounceCallback(handleChange, 300);

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    const initialConfig = useMemo(
        () => createEditorConfig(value),
        [
            /**
             * we don't want to re-create the editor config when the value changes,
             * value updates are handled by ValueSyncPlugin
             */
        ],
    );

    return (
        <div
            className={clsx(
                'flex flex-col overflow-hidden font-sans',
                staticToolbar ? '[&>#toolbar]:flex' : '[&>#toolbar]:hidden',
                '[&:focus-within_#toolbar]:flex',
            )}
        >
            <LexicalComposer initialConfig={initialConfig}>
                {toolbar}
                <div
                    className={clsx(
                        'bg-white-200 min-h-[200px] p-l relative',
                        '[&_.editor-heading-h1]:text-2xl [&_.editor-heading-h1]:font-medium [&_.editor-heading-h1]:leading-[2rem] [&_.editor-heading-h1]:mb-m [&_.editor-heading-h1]:mt-0',
                        '[&_.editor-heading-h2]:text-xl [&_.editor-heading-h2]:font-medium [&_.editor-heading-h2]:leading-[2rem] [&_.editor-heading-h2]:mb-m [&_.editor-heading-h2]:mt-0',
                        '[&_.editor-heading-h3]:text-lg [&_.editor-heading-h3]:font-medium [&_.editor-heading-h3]:leading-[1.625rem] [&_.editor-heading-h3]:mb-m [&_.editor-heading-h3]:mt-0',
                        '[&_.editor-hr]:border-none [&_.editor-hr]:border-t [&_.editor-hr]:border-gray-400 [&_.editor-hr]:my-l',
                        '[&_.editor-list-ol]:list-decimal [&_.editor-list-ol]:ml-xl [&_.editor-list-ol]:pl-l',
                        '[&_.editor-list-ul]:list-disc [&_.editor-list-ul]:ml-xl [&_.editor-list-ul]:pl-l',
                        '[&_.editor-listitem]:mb-xs',
                        '[&_.editor-paragraph]:mb-s [&_.editor-paragraph]:mt-0',
                        '[&_.editor-text-bold]:font-semibold',
                        '[&_.editor-text-underline]:underline',
                    )}
                >
                    <RichTextPlugin
                        contentEditable={
                            <ContentEditable
                                aria-placeholder={placeholder}
                                className="text-gray-900 text-sm font-normal leading-[1.25rem] min-h-[150px] outline-none focus:outline-none"
                                disabled={disabled}
                                placeholder={
                                    <div className="text-gray-600 text-sm font-normal leading-[1.25rem] left-l pointer-events-none absolute top-xl">
                                        {placeholder}
                                    </div>
                                }
                            />
                        }
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                    <HistoryPlugin />
                    <ListPlugin />
                    <HorizontalRulePlugin />
                    <ValueSyncPlugin value={value} />
                    <OnChangePlugin ignoreSelectionChange={true} onChange={debouncedChange} />
                </div>
            </LexicalComposer>
        </div>
    );
};
